import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';

type Variant = 'primary' | 'secondary' | 'ghost';

interface ButtonProps {
  children: React.ReactNode;
  /** Internal route: renders a react-router <Link>. */
  to?: string;
  /** External / protocol link (https, mailto, tel): renders an <a>. */
  href?: string;
  onClick?: () => void;
  variant?: Variant;
  /** Show the trailing arrow icon (default true). */
  withArrow?: boolean;
  className?: string;
  ariaLabel?: string;
  type?: 'button' | 'submit';
}

/**
 * Single reusable call-to-action. Renders as a router Link, an anchor, or a
 * button depending on the props supplied, but always shares one visual style.
 */
export const Button: React.FC<ButtonProps> = ({
  children,
  to,
  href,
  onClick,
  variant = 'primary',
  withArrow = true,
  className = '',
  ariaLabel,
  type = 'button',
}) => {
  const classes = `ds-btn ds-btn--${variant} ${className}`.trim();
  const content = (
    <>
      {children}
      {withArrow && <FiArrowRight className="ds-btn__icon" aria-hidden="true" />}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={classes} aria-label={ariaLabel} onClick={onClick}>
        {content}
      </Link>
    );
  }

  if (href) {
    const isExternal = href.startsWith('http');
    return (
      <a
        href={href}
        className={classes}
        aria-label={ariaLabel}
        onClick={onClick}
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {content}
      </a>
    );
  }

  return (
    <button type={type} className={classes} onClick={onClick} aria-label={ariaLabel}>
      {content}
    </button>
  );
};

export default Button;
